/**
 * Köşedeki pomodoro göstergesi ve mola uyarısı (services/pomodoro.ts). App kökünde.
 * Çalışırken küçük hap (🍅 kalan süre / 🌿 mola), bitince ya da 45 dk kesintisiz çalışınca küçük kart:
 * "5 dk dinlen" / "10 dk bu konunun oyununu oyna" / "devam".
 */
import React, { useEffect, useState } from 'react';
import {
    BREAK_MIN, FOCUS_MIN, LONG_BREAK_MIN, NUDGE_MIN, PomoState, dismissAlert, getStudyContext, setNudgeOff, startBreak, startFocus,
    stopPomodoro, subscribePomodoro,
} from '../services/pomodoro';

const SUBJECT_LABEL: Record<string, string> = { turkish: 'Türkçe', math: 'Matematik', history: 'Tarih', geography: 'Coğrafya', citizenship: 'Vatandaşlık' };

const fmt = (ms: number) => { const s = Math.max(0, Math.round(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };

const PomodoroWidget: React.FC = () => {
    const [s, setS] = useState<PomoState | null>(null);
    const [, setTick] = useState(0);
    useEffect(() => subscribePomodoro(setS), []);
    useEffect(() => {
        if (!s || s.phase === 'idle') return;
        const t = window.setInterval(() => setTick(x => x + 1), 1000);
        return () => window.clearInterval(t);
    }, [s?.phase]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!s) return null;

    const subject = getStudyContext().subject || s.subject;
    const playGame = () => {
        startBreak(10);
        try { window.dispatchEvent(new CustomEvent('kpss-pomodoro-game', { detail: { subject } })); } catch { /* yok */ }
    };
    const longBreak = s.count > 0 && s.count % 4 === 0;

    if (s.alert) {
        const title = s.alert === 'focus-done' ? `🍅 Pomodoro bitti! (bugün ${s.count})` : s.alert === 'break-done' ? '🌿 Mola bitti' : `⏰ ${NUDGE_MIN} dakikadır çalışıyorsun`;
        const body = s.alert === 'focus-done'
            ? (longBreak ? `4 pomodoro oldu: ${LONG_BREAK_MIN} dk uzun mola ver.` : `${BREAK_MIN} dk mola ver: kalk, su iç, gözlerini dinlendir.`)
            : s.alert === 'break-done' ? `Yeni bir ${FOCUS_MIN} dakikalık odak?` : 'Beyin dinlenince daha iyi öğrenir: 5 dk dinlen ya da 10 dk bu konunun oyununu oyna.';
        return (
            <div className="fixed z-[10001] left-3 w-[min(300px,calc(var(--vw-full)-24px))] rounded-2xl bg-slate-900/95 border border-amber-300/40 shadow-2xl p-3 text-white"
                style={{ bottom: 'calc(var(--sab) + 12px)' }} role="status">
                <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-black">{title}</div>
                        <div className="text-xs text-slate-300 mt-0.5">{body}</div>
                    </div>
                    <button onClick={dismissAlert} aria-label="Kapat" className="text-slate-400 text-sm leading-none px-1">✕</button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                    {s.alert === 'break-done'
                        ? <button onClick={() => startFocus(s.label, s.subject)} className="px-2.5 py-1.5 rounded-lg bg-rose-500 text-white text-xs font-black">🍅 Başlat</button>
                        : <button onClick={() => startBreak(s.alert === 'nudge' ? BREAK_MIN : undefined)} className="px-2.5 py-1.5 rounded-lg bg-emerald-400 text-emerald-950 text-xs font-black">🌿 {s.alert === 'nudge' ? BREAK_MIN : longBreak ? LONG_BREAK_MIN : BREAK_MIN} dk mola</button>}
                    {s.alert !== 'break-done' && subject && <button onClick={playGame} className="px-2.5 py-1.5 rounded-lg bg-violet-500 text-white text-xs font-black">🎮 10 dk {SUBJECT_LABEL[subject] || ''} oyunu</button>}
                    <button onClick={dismissAlert} className="px-2.5 py-1.5 rounded-lg bg-white/10 text-xs font-bold">Devam</button>
                </div>
                {s.alert === 'nudge' && <button onClick={() => setNudgeOff(true)} className="mt-1.5 text-[10px] text-slate-500 underline">Bu uyarıyı kapat (Çalışma Planım › Saatlerim'den açılır)</button>}
            </div>
        );
    }

    if (s.phase === 'idle') return null;
    const left = s.endsAt - Date.now();
    const focus = s.phase === 'focus';
    return (
        <div className={`fixed z-[10001] left-3 flex items-center gap-1.5 rounded-full pl-3 pr-1 py-1 shadow-xl border text-white text-xs font-black ${focus ? 'bg-rose-600/95 border-rose-300/50' : 'bg-emerald-600/95 border-emerald-300/50'}`}
            style={{ bottom: 'calc(var(--sab) + 12px)' }} title={s.label || ''}>
            <span>{focus ? '🍅' : '🌿'} {fmt(left)}</span>
            {focus && s.label && <span className="hidden sm:inline max-w-[160px] truncate font-semibold opacity-90">{s.label}</span>}
            <button onClick={stopPomodoro} aria-label="Durdur" className="w-6 h-6 rounded-full bg-black/20 leading-none">✕</button>
        </div>
    );
};

export default PomodoroWidget;
